/**
 * ShareButtons Component
 *
 * Reusable social share buttons for content pages (blog posts, recipes, gear reviews).
 * Uses native share URLs — no third-party SDK dependencies.
 *
 * Supported platforms:
 * - Copy Link (navigator.clipboard)
 * - Twitter/X (intent URL)
 * - Facebook (sharer URL)
 * - Pinterest (create-pin URL)
 *
 * @example
 * <ShareButtons
 *   url="https://backyahdbbq.com/guides/brisket-trim"
 *   title="Foolproof Brisket Trim"
 *   image="https://backyahdbbq.com/media/blog/brisket-trim/cover.jpg"
 * />
 */

import { useState } from 'react';
import { Link2, Check } from 'lucide-react';

interface ShareButtonsProps {
  /** Full URL to share */
  url: string;
  /** Title/text to share */
  title: string;
  /** Optional image URL. When set, enables a richer Pinterest pin. */
  image?: string;
}

/**
 * Renders inline SVG icon for X/Twitter.
 * Uses the same SVG path as SocialIcons.tsx for visual consistency.
 */
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/**
 * Renders inline SVG icon for Facebook.
 * Uses the same SVG path as SocialIcons.tsx for visual consistency.
 */
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

/**
 * Renders inline SVG icon for Pinterest.
 */
function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C24.014 5.367 18.635.001 12.017.001z" />
    </svg>
  );
}

export function ShareButtons({ url, title, image }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const pinterestShareUrl =
    `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}` +
    (image ? `&media=${encodeURIComponent(image)}` : '');

  function handleCopyLink() {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="flex items-center gap-2 no-print" data-testid="share-buttons">
      <span className="text-sm text-gray-500 mr-1">Share:</span>

      {/* Copy Link */}
      <button
        onClick={handleCopyLink}
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
        aria-label={copied ? 'Link copied' : 'Copy link'}
        title={copied ? 'Copied!' : 'Copy link'}
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Link2 className="w-4 h-4" />}
      </button>

      {/* Twitter/X */}
      <a
        href={twitterShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
        aria-label="Share on X (Twitter)"
        title="Share on X"
      >
        <XIcon className="w-4 h-4" />
      </a>

      {/* Facebook */}
      <a
        href={facebookShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
        aria-label="Share on Facebook"
        title="Share on Facebook"
      >
        <FacebookIcon className="w-4 h-4" />
      </a>

      {/* Pinterest */}
      <a
        href={pinterestShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
        aria-label="Save to Pinterest"
        title="Save to Pinterest"
      >
        <PinterestIcon className="w-4 h-4" />
      </a>
    </div>
  );
}
